import { ESX_COMPATIBLE_PRODUCTS } from './components/Product/ProductList.constants';

export const PANEL_TYPES = {
  PRE_PANEL: 'PRE_PANEL',
  PROPERTY_DATA: 'PROPERTY_DATA',
  STRUCTURE_MEASUREMENTS: 'STRUCTURE_MEASUREMENTS',
};

function collapseWhitespace(value) {
  return Array.from(value).reduce((acc, char) => {
    if (char.trim() === '') {
      return { ...acc, pendingSpace: true };
    }

    return {
      result: `${acc.result}${acc.pendingSpace && acc.result ? ' ' : ''}${char}`,
      pendingSpace: false,
    };
  }, { result: '', pendingSpace: false }).result.trim();
}

/**
 * Aligns subscription product labels from API / place-order response with PRIMARY_SUBSCRIPTION_PRODUCTS.name.
 * e.g. "Walls, Windows & Doors" vs "Walls, Windows, Doors" (ampersand vs commas) — otherwise WWD-only UI (usage bar, etc.) fails to match.
 */
export function normalizeSubscriptionProductLabelForMatch(name) {
  if (name == null || typeof name !== 'string') return '';
  const commaSeparatedName = name
    .toLowerCase()
    .split('&')
    .map(collapseWhitespace)
    .join(', ');
  const productNames = commaSeparatedName
    .split(',')
    .map(collapseWhitespace)
    .map((part) => (part.startsWith('and ') ? collapseWhitespace(part.slice(4)) : part));

  return productNames.join(', ').trim();
}

export const PRIMARY_SUBSCRIPTION_PRODUCTS = [
  {
    productId: 106,
    subscriptionSkuName: 'Subscription to Roof',
    name: 'Roof',
    unitBadgeCount: 1,
    description: 'Win the bid with Bid Perfect™, then upgrade to a discounted Premium Roof Report '
      + 'to get highly accurate measurements for precise material ordering.',
    price: '',
    unitCount: '',
    isSubscription: null,
    usedQty: 0,
    includedQty: 0,
    isProductActive: false,
  },
  {
    productId: 107,
    subscriptionSkuName: 'Subscription to Walls, Windows, and Doors',
    name: 'Walls, Windows, Doors',
    unitBadgeCount: 1,
    description: 'Get detailed measurements and 3D wall diagrams in a format that\'s easy to use and understand.',
    price: '',
    unitCount: '',
    isSubscription: null,
    usedQty: 0,
    includedQty: 0,
    isProductActive: false,
  },
  {
    productId: 108,
    subscriptionSkuName: 'Subscription to Walls',
    name: 'Walls',
    unitBadgeCount: 1,
    description: 'Quickly quote homeowners for exterior repairs with preliminary sales data for exterior walls, '
      + 'including distinct siding and masonry measurements.',
    price: '',
    unitCount: '',
    isSubscription: null,
    usedQty: 0,
    includedQty: 0,
    isProductActive: false,
  },
  {
    productId: 110,
    subscriptionSkuName: 'Subscription to Bid Perfect',
    name: 'Bid Perfect',
    unitBadgeCount: 1,
    description: 'Easily access the preliminary roof data and measurements you need to swiftly create winning roof estimates.',
    price: '',
    unitCount: '',
    isSubscription: null,
    usedQty: 0,
    includedQty: 0,
    isProductActive: false,
  },
  {
    productId: 111,
    subscriptionSkuName: 'Subscription to Full House',
    name: 'Full House',
    unitBadgeCount: 2,
    description: 'Get all of the roof, windows, walls, and doors measurements you need to bid, order, and upsell - in a single report.',
    price: '',
    unitCount: '',
    isSubscription: null,
    usedQty: 0,
    includedQty: 0,
    isProductActive: false,
  },
];

export const ADDON_SUBSCRIPTION_PRODUCTS = [
  {
    productId: 114,
    name: 'ESX',
    price: '',
    label: '',
  },
];

export const FULL_HOUSE_PRODUCT_ID = 111;

// #genai
/**
 * Maps subscriptionSkuName (from subscription API) to product IDs.
 * Single product = Single Plan; multiple products = Bundle plan.
 */
export const SUBSCRIPTION_SKU_NAME_TO_PRODUCT_IDS = {
  'Subscription to Walls': [108],
  'Subscription to Full House': [111],
  'Subscription to Bid Perfect': [110],
  'Subscription to Walls, Windows, and Doors': [107],
  'Subscription to Roof': [106],
  'Subscription to Roof, Walls, Walls Windows Doors, and Full House': [106, 107, 108, 111],
};

/**
 * Returns product IDs for a subscription SKU name from the API.
 * Single ID = Single Plan; multiple IDs = Bundle plan. Returns [] if unknown.
 * @param {string} subscriptionSkuName - subscriptionSkuName from subscription API
 * @returns {number[]} product IDs for that subscription
 */
export function getProductIdsBySubscriptionSkuName(subscriptionSkuName) {
  if (subscriptionSkuName == null || typeof subscriptionSkuName !== 'string') return [];
  const key = subscriptionSkuName.trim();
  return SUBSCRIPTION_SKU_NAME_TO_PRODUCT_IDS[key] ?? [];
}

export const REGULAR_DELIVERY_PRODUCT_ID = 8;
export const SUBSCRIPTION_DELIVERY_PRODUCT_IDS = [4, 5, 7];

export const DELIVERY_SUBSCRIPTION_PRODUCTS = [
  {
    productId: 4,
    name: 'Express Delivery',
    price: '',
    label: '',
  },
  {
    productId: 5,
    name: 'Same Day Delivery',
    price: '',
    label: '',
  },
  {
    productId: 7,
    name: '3 Hour Delivery',
    price: '',
    label: '',
  },
];

/**
 * Subscription V2 ESX add-on price.
 * Products API returns the base ESX price; Full House is charged 2× (same rule as classic getAdjustedProductPrice).
 */
export function getSubscriptionEsxPrice(subscriptionProduct) {
  const base = Number(subscriptionProduct?.esxPrice) || 0;
  if (base <= 0) return 0;
  const productId = Number(subscriptionProduct?.productId ?? subscriptionProduct?.id);
  return productId === FULL_HOUSE_PRODUCT_ID ? base * 2 : base;
}

/**
 * Subscription V2 estimated total (order page and upgrade flow).
 * Computed locally instead of the estimated-cost API.
 * Uses partial overage (subscription units × overageRatePerUnit) + delivery upgrade + ESX add-on.
 * Subscription units = pin count × unitBadgeCount (bundle Full House = 2 units per pin).
 * Delivery upgrades and ESX add-on are charged per ordered structure (pin count).
 * `orderedUnits` is the pin/report count (order page: map pin count; upgrade: 1).
 * Returns [min, max] to be compatible with getEstimatedTotalText.
 */
export function getSubscriptionEstimatedTotal({
  subscriptionProduct,
  selectedPrimaryDeliveryProduct,
  selectedOtherProducts,
  orderedUnits = 1,
}) {
  if (!subscriptionProduct) return [0, 0];

  const pinCount = Math.max(Number(orderedUnits) || 1, 1);
  const selectedPrimaryDeliveryId = Number(selectedPrimaryDeliveryProduct ?? REGULAR_DELIVERY_PRODUCT_ID);
  const selectedDelivery = (subscriptionProduct.allowedDeliveries ?? []).find(
    (d) => Number(d.productId) === selectedPrimaryDeliveryId,
  );
  const deliveryCostPerPin = selectedPrimaryDeliveryId === REGULAR_DELIVERY_PRODUCT_ID
    ? 0
    : (Number(selectedDelivery?.price) || 0);
  const deliveryCost = deliveryCostPerPin * pinCount;

  const unitBadgeMultiplier = Math.max(Number(subscriptionProduct.unitBadgeCount ?? 1) || 1, 1);
  const units = pinCount * unitBadgeMultiplier;
  const usedQty = Number(subscriptionProduct.usedQty) || 0;
  const includedQty = Number(subscriptionProduct.includedQty) || 0;
  const overageRatePerUnit = Number(subscriptionProduct.overageRatePerUnit) || 0;
  const remainingIncludedUnits = Math.max(includedQty - usedQty, 0);
  const overageUnits = includedQty > 0 ? Math.max(units - remainingIncludedUnits, 0) : 0;
  const overageCost = overageRatePerUnit > 0 ? (overageUnits * overageRatePerUnit) : 0;

  const selectedAddonIds = Object.keys(selectedOtherProducts ?? {}).map(Number);
  const hasSelectedEsx = selectedAddonIds.includes(114);
  const primarySubscriptionId = Number(subscriptionProduct.productId);
  const esxCostPerPin = hasSelectedEsx && ESX_COMPATIBLE_PRODUCTS.includes(primarySubscriptionId)
    ? getSubscriptionEsxPrice(subscriptionProduct)
    : 0;
  const esxCost = esxCostPerPin * pinCount;

  const total = Number((overageCost + deliveryCost + esxCost).toFixed(2));
  return [total, total];
}
